import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Mail, CheckCircle, XCircle, HelpCircle, Eye, Reply, Send,
  FileText, Clock, Hash, Activity, ArrowRight, ExternalLink,
  AlertCircle, Inbox, Zap, MessageSquare, MailOpen, Pencil,
} from 'lucide-react';
import { emailService } from '@core/supabase/services/emailService';
import { EmailThread } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

/* ─── Constants ──────────────────────────────────────────────── */
const BRAND = '#8B80F9';

const CTA_ICON = {
  accept: <CheckCircle className="w-3.5 h-3.5 text-green-400" />,
  reject: <XCircle className="w-3.5 h-3.5 text-red-400" />,
  query:  <HelpCircle className="w-3.5 h-3.5 text-yellow-400" />,
};
const CTA_LABEL = { accept: 'Shortlisted', reject: 'Passed', query: 'Query' };
const CTA_COLOR = { accept: '#22c55e', reject: '#ef4444', query: '#f59e0b' };

type ThreadTab = 'overview' | 'email' | 'parameters';
const TABS: { id: ThreadTab; label: string }[] = [
  { id: 'overview',   label: 'Overview'   },
  { id: 'email',      label: 'Email'      },
  { id: 'parameters', label: 'Parameters' },
];

const DT = 'vscode-text-secondary pr-2 min-w-0';
const DD = 'vscode-text-primary min-w-0 break-words';

function fmt(iso: string | null | undefined): string {
  const s = formatDateTimeGmt(iso);
  return s === '—' && iso ? iso : s;
}
function fmtShort(iso: string | null | undefined): string {
  return formatDateTimeGmt(iso);
}

/* ─── Step accordion ─────────────────────────────────────────── */
interface StepItem {
  key: string;
  stepNum: number;
  label: string;
  sublabel?: string;
  timestamp?: string | null;
  color: string;
  isDone: boolean;
  content?: React.ReactNode;
}

function StepAccordion({ steps }: { steps: StepItem[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(steps.map((s, i) => [s.key, i === 0]))
  );
  const toggle = (k: string) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="space-y-0">
      {steps.map((step, i) => {
        const isOpen = open[step.key] ?? false;
        return (
          <div key={step.key} className="flex gap-3 pb-1">
            {/* Connector column */}
            <div className="flex flex-col items-center shrink-0 pt-3">
              <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 border-2"
                style={{
                  borderColor: step.color,
                  background: step.isDone ? `${step.color}20` : 'transparent',
                }}>
                <span className="text-[10px] font-bold" style={{ color: step.color }}>{step.stepNum}</span>
              </div>
              {i < steps.length - 1 && (
                <div className="w-px flex-1 mt-1" style={{ background: `${step.color}30`, minHeight: 20 }} />
              )}
            </div>

            {/* Card */}
            <div className="flex-1 min-w-0 mb-3">
              <button type="button"
                className="w-full flex items-start justify-between gap-2 text-left px-4 py-3 rounded-xl border vscode-border hover:vscode-bg-hover transition-colors"
                style={isOpen ? { borderColor: `${step.color}40`, background: `${step.color}08` } : {}}
                onClick={() => step.content && toggle(step.key)}>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: step.color }}>
                    Step {step.stepNum}
                  </p>
                  <p className="text-sm font-semibold vscode-text-primary leading-tight">{step.label}</p>
                  {step.sublabel && <p className="text-xs vscode-text-tertiary mt-0.5">{step.sublabel}</p>}
                  {step.timestamp && <p className="text-[10px] vscode-text-tertiary mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" />{fmt(step.timestamp)}</p>}
                </div>
                {step.content && (
                  <span className="text-[10px] vscode-text-tertiary shrink-0 mt-1 px-2 py-0.5 rounded border vscode-border">
                    {isOpen ? '▲ Hide' : '▼ Show'}
                  </span>
                )}
              </button>
              {isOpen && step.content && (
                <div className="mt-1 px-4 py-3 rounded-xl border vscode-border vscode-bg-elevated">
                  {step.content}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────── */
export function EmailThreadPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const [thread, setThread] = useState<EmailThread | null>(null);
  const [replies, setReplies] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(true);

  const rawTab = searchParams.get('tab');
  const activeTab: ThreadTab = (TABS.map((t) => t.id) as string[]).includes(rawTab ?? '') ? (rawTab as ThreadTab) : 'overview';
  const setTab = (t: ThreadTab) =>
    setSearchParams((p) => { const n = new URLSearchParams(p); t === 'overview' ? n.delete('tab') : n.set('tab', t); return n; }, { replace: true });

  useEffect(() => {
    if (!id) return;
    emailService.getById(id).then((t) => {
      setThread(t);
      if (t?.application_id) {
        emailService.listByApplication(t.application_id).then((all) => {
          setReplies(all.filter((r) => r.in_reply_to_thread_id === id)
            .sort((a, b) => new Date(a.sent_at ?? a.created_at).getTime() - new Date(b.sent_at ?? b.created_at).getTime()));
        }).catch(() => setReplies([]));
      }
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <FullPageSpinner />;
  if (!thread) {
    return (
      <div className="p-8 text-center vscode-text-tertiary">
        <p>Thread not found.</p>
        <Link to="/emails" className="mt-3 inline-flex items-center gap-1.5 text-sm" style={{ color: BRAND }}>
          <ArrowRight className="w-4 h-4" /> Back to Email Activity
        </Link>
      </div>
    );
  }

  const app = thread.application as { id?: string; client?: { name: string }; candidate?: { full_name: string; id?: string }; job?: { title: string; id?: string } } | undefined;
  const clientName = app?.client?.name ?? thread.recipient_name ?? '—';
  const candidateJob = [app?.candidate?.full_name, app?.job?.title].filter(Boolean).join(' — ') || '—';
  const breadcrumbLabel = thread.subject || candidateJob;

  const statusLabel = thread.cta_response ? CTA_LABEL[thread.cta_response] : thread.sent_at ? 'Awaiting reply' : 'Draft';
  const statusColor = thread.cta_response ? CTA_COLOR[thread.cta_response] : BRAND;
  const canFollowUp = !thread.cta_response && !!thread.sent_at && !thread.followup_stopped && (thread.followup_count ?? 0) < 3;

  /* ── Build accordion steps ── */
  const steps: StepItem[] = [];
  let stepNum = 1;

  // Step 1: Sent
  steps.push({
    key: 'sent', stepNum: stepNum++,
    label: 'Email Sent',
    sublabel: `To: ${thread.recipient_name || thread.recipient_email}`,
    timestamp: thread.sent_at,
    color: thread.sent_at ? '#22c55e' : '#6b7280',
    isDone: !!thread.sent_at,
    content: (
      <div className="space-y-3">
        <dl className="ggj-attr-grid">
          <dt className={DT}>Subject</dt><dd className={DD}>{thread.subject || '(No subject)'}</dd>
          <dt className={DT}>To</dt>
          <dd className={DD}>
            <a href={`mailto:${thread.recipient_email}`} className="hover:underline" style={{ color: BRAND }}>{thread.recipient_email}</a>
            {thread.recipient_name && <span className="vscode-text-tertiary ml-1">({thread.recipient_name})</span>}
          </dd>
          <dt className={DT}>Sent at</dt><dd className={DD}>{fmt(thread.sent_at)}</dd>
        </dl>
        <div className="rounded-xl border vscode-border overflow-hidden">
          <div className="px-4 py-2.5 vscode-bg-tertiary border-b vscode-border flex items-center gap-2">
            <Mail className="w-3 h-3 vscode-text-tertiary" />
            <span className="text-xs vscode-text-tertiary">Email body</span>
          </div>
          <div className="px-4 py-3 max-h-60 overflow-y-auto">
            {thread.body_html
              ? <div className="text-sm vscode-text-primary" dangerouslySetInnerHTML={{ __html: thread.body_html }} />
              : <p className="text-sm vscode-text-tertiary italic">No body content.</p>}
          </div>
        </div>
      </div>
    ),
  });

  // Step 2: Opened
  if (thread.opened_at || thread.open_count > 0) {
    steps.push({
      key: 'opened', stepNum: stepNum++,
      label: 'Email Opened',
      sublabel: thread.open_count > 1 ? `${thread.open_count}× opened` : 'Opened once',
      timestamp: thread.opened_at,
      color: '#3b82f6', isDone: true,
      content: (
        <dl className="ggj-attr-grid">
          <dt className={DT}>First opened</dt><dd className={DD}>{fmt(thread.opened_at)}</dd>
          <dt className={DT}>Total opens</dt><dd className={DD}>{thread.open_count}</dd>
        </dl>
      ),
    });
  }

  // Follow-up steps
  if ((thread.followup_count ?? 0) > 0) {
    steps.push({
      key: 'followup', stepNum: stepNum++,
      label: `Follow-up${(thread.followup_count ?? 0) > 1 ? 's' : ''} Sent`,
      sublabel: `${thread.followup_count} follow-up${(thread.followup_count ?? 0) > 1 ? 's' : ''}`,
      timestamp: thread.next_followup_at,
      color: '#f59e0b', isDone: true,
      content: (
        <dl className="ggj-attr-grid">
          <dt className={DT}>Follow-ups sent</dt><dd className={DD}>{thread.followup_count}</dd>
          <dt className={DT}>Next follow-up</dt><dd className={DD}>{thread.next_followup_at ? fmt(thread.next_followup_at) : thread.followup_stopped ? 'Stopped' : '—'}</dd>
          <dt className={DT}>Auto follow-up</dt><dd className={DD}>{thread.followup_stopped ? 'Stopped' : 'Active'}</dd>
        </dl>
      ),
    });
  }

  // Reply steps
  replies.forEach((r, i) => {
    steps.push({
      key: `reply-${r.id}`, stepNum: stepNum++,
      label: `Reply ${i + 1}`,
      sublabel: r.recipient_name || r.recipient_email,
      timestamp: r.sent_at,
      color: BRAND, isDone: true,
      content: (
        <div className="space-y-2">
          <dl className="ggj-attr-grid mb-2">
            <dt className={DT}>Subject</dt><dd className={DD}>{r.subject || '(No subject)'}</dd>
            <dt className={DT}>To</dt><dd className={DD}>{r.recipient_name || r.recipient_email}</dd>
            <dt className={DT}>Sent at</dt><dd className={DD}>{fmt(r.sent_at)}</dd>
          </dl>
          {r.body_html && (
            <div className="rounded-xl border vscode-border overflow-hidden">
              <div className="px-4 py-2 vscode-bg-tertiary border-b vscode-border flex items-center gap-1.5">
                <MessageSquare className="w-3 h-3 vscode-text-tertiary" />
                <span className="text-xs vscode-text-tertiary">Reply body</span>
              </div>
              <div className="px-4 py-3 max-h-48 overflow-y-auto">
                <div className="text-sm vscode-text-primary" dangerouslySetInnerHTML={{ __html: r.body_html }} />
              </div>
            </div>
          )}
        </div>
      ),
    });
  });

  // CTA response or Awaiting
  if (thread.cta_response) {
    steps.push({
      key: 'cta', stepNum: stepNum++,
      label: `Client Responded — ${CTA_LABEL[thread.cta_response]}`,
      sublabel: thread.cta_response_note ?? undefined,
      timestamp: thread.cta_responded_at,
      color: CTA_COLOR[thread.cta_response], isDone: true,
      content: (
        <dl className="ggj-attr-grid">
          <dt className={DT}>Response</dt>
          <dd className={DD}>
            <span className="inline-flex items-center gap-1.5" style={{ color: CTA_COLOR[thread.cta_response] }}>
              {CTA_ICON[thread.cta_response]} {CTA_LABEL[thread.cta_response]}
            </span>
          </dd>
          <dt className={DT}>Responded at</dt><dd className={DD}>{fmt(thread.cta_responded_at)}</dd>
          {thread.cta_response_note && (<><dt className={DT}>Note</dt><dd className={DD}>{thread.cta_response_note}</dd></>)}
        </dl>
      ),
    });
  } else if (thread.sent_at) {
    steps.push({
      key: 'awaiting', stepNum: stepNum++,
      label: 'Awaiting Reply',
      sublabel: 'No CTA response yet',
      timestamp: null,
      color: '#6b7280', isDone: false,
      content: (
        <div className="space-y-2">
          <p className="text-xs vscode-text-secondary">The client has not responded to the CTA link yet.</p>
          {canFollowUp && app?.id && (
            <Link to={`/emails/compose?applicationId=${app.id}&replyToThread=${thread.id}`}
              className="text-xs inline-flex items-center gap-1.5 font-medium" style={{ color: BRAND }}>
              <Reply className="w-3 h-3" /> Send follow-up
            </Link>
          )}
        </div>
      ),
    });
  }

  /* ── Render ── */
  return (
    <div className="w-full pb-8">

      <PageHeader
        icon={Mail}
        breadcrumb={[
          { label: 'Pipeline', href: '/applications' },
          { label: 'Email activity', href: '/emails' },
          { label: breadcrumbLabel },
        ]}
        title={thread.subject || '(No subject)'}
        subtitle={candidateJob !== '—' ? candidateJob : clientName}
      />

      {/* Tabs */}
      <nav className="ggj-cand-tablist mb-0" aria-label="Thread sections">
        {TABS.map((t) => (
          <button key={t.id} type="button"
            className={`ggj-cand-tab${activeTab === t.id ? ' ggj-cand-tab--active' : ''}`}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </nav>

      {/* Toolbar */}
      <div className="ggj-profile-toolbar mb-5 flex-wrap gap-y-1">
        {app?.id && (
          <Link to={`/emails/compose?applicationId=${app.id}&replyToThread=${thread.id}`}
            className="ggj-toolbar-btn inline-flex items-center gap-1.5">
            <Reply className="w-3.5 h-3.5" /> Reply
          </Link>
        )}
        {canFollowUp && app?.id && (
          <Link to={`/emails/compose?applicationId=${app.id}&replyToThread=${thread.id}`}
            className="ggj-toolbar-btn inline-flex items-center gap-1.5">
            <Send className="w-3.5 h-3.5" /> Send Follow-up
          </Link>
        )}
        {app?.id && (
          <Link to={`/applications/${app.id}`}
            className="ggj-toolbar-btn inline-flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> View Application
          </Link>
        )}
        {activeTab === 'parameters' && app?.id && (
          <Link to={`/applications/${app.id}?edit=1`}
            className="ggj-toolbar-btn inline-flex items-center gap-1.5"
            style={{ color: BRAND }}>
            <Pencil className="w-3.5 h-3.5" aria-hidden /> Edit application
          </Link>
        )}
        <button type="button"
          className="ggj-toolbar-btn inline-flex items-center gap-1.5 ml-auto"
          onClick={() => navigate(returnTo || '/emails', { replace: !returnTo })}>
          <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Back to Email Activity
        </button>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">

        <div className="min-w-0 space-y-4">

          {/* ════ OVERVIEW TAB ════ */}
          {activeTab === 'overview' && (
            <>
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" style={{ color: BRAND }} /> Thread Summary
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={DT}>Status</dt>
                    <dd className={DD}>
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ background: `${statusColor}18`, color: statusColor }}>
                        {thread.cta_response && CTA_ICON[thread.cta_response]}
                        {statusLabel}
                      </span>
                    </dd>
                    <dt className={DT}>Subject</dt><dd className={DD}>{thread.subject || '(No subject)'}</dd>
                    <dt className={DT}>Recipient</dt>
                    <dd className={DD}>
                      <a href={`mailto:${thread.recipient_email}`} className="hover:underline" style={{ color: BRAND }}>
                        {thread.recipient_email}
                      </a>
                      {thread.recipient_name && <span className="vscode-text-tertiary ml-1">({thread.recipient_name})</span>}
                    </dd>
                    <dt className={DT}>Client</dt><dd className={DD}>{clientName}</dd>
                    <dt className={DT}>Candidate · Job</dt><dd className={DD}>{candidateJob}</dd>
                    <dt className={DT}>Sent at</dt><dd className={DD}>{fmt(thread.sent_at)}</dd>
                    <dt className={DT}>Replies</dt><dd className={DD}>{replies.length}</dd>
                  </dl>
                </div>
              </div>

              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" style={{ color: BRAND }} /> Open Tracking & Follow-ups
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={DT}>Total opens</dt>
                    <dd className={DD}>
                      <span className="font-bold" style={{ color: thread.open_count > 0 ? '#3b82f6' : undefined }}>
                        {thread.open_count > 0 ? `${thread.open_count}×` : 'Not opened'}
                      </span>
                    </dd>
                    <dt className={DT}>Last opened</dt><dd className={DD}>{thread.opened_at ? fmt(thread.opened_at) : '—'}</dd>
                    <dt className={DT}>Follow-ups sent</dt><dd className={DD}>{thread.followup_count ?? 0}</dd>
                    <dt className={DT}>Auto follow-up</dt><dd className={DD}>{thread.followup_stopped ? 'Stopped' : 'Active'}</dd>
                    <dt className={DT}>Next follow-up</dt><dd className={DD}>{thread.next_followup_at ? fmt(thread.next_followup_at) : '—'}</dd>
                  </dl>
                </div>
              </div>

              {thread.cta_response && (
                <div className="ggj-param-group overflow-hidden">
                  <div className="ggj-profile-section-h flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5" style={{ color: statusColor }} /> Client Response
                  </div>
                  <div className="ggj-profile-section-body">
                    <dl className="ggj-attr-grid">
                      <dt className={DT}>Response</dt>
                      <dd className={DD}>
                        <span className="inline-flex items-center gap-1.5" style={{ color: statusColor }}>
                          {CTA_ICON[thread.cta_response]} {CTA_LABEL[thread.cta_response]}
                        </span>
                      </dd>
                      <dt className={DT}>Responded at</dt><dd className={DD}>{fmt(thread.cta_responded_at)}</dd>
                      {thread.cta_response_note && (<><dt className={DT}>Note</dt><dd className={DD}>{thread.cta_response_note}</dd></>)}
                    </dl>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ════ EMAIL TAB ════ */}
          {activeTab === 'email' && (
            <div className="space-y-4">
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" style={{ color: BRAND }} /> Communication Timeline
                  <span className="ml-auto text-[10px] vscode-text-tertiary font-normal">{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="ggj-profile-section-body">
                  {/* Reverse so latest event is Step 1 at the top */}
                  <StepAccordion steps={[...steps].reverse().map((s, i) => ({ ...s, stepNum: i + 1 }))} />
                </div>
              </div>
            </div>
          )}

          {/* ════ PARAMETERS TAB ════ */}
          {activeTab === 'parameters' && (
            <div className="space-y-4">
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-param-section-block border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
                  <div className="ggj-profile-section-h">Object</div>
                  <div className="ggj-profile-section-body">
                    <dl className="ggj-attr-grid">
                      <dt className={DT}>Name</dt>
                      <dd className={`${DD} font-medium`}>{thread.subject?.trim() || candidateJob}</dd>
                      <dt className={DT}>Object type</dt>
                      <dd className={`${DD} vscode-text-secondary`}>Email thread (read-only)</dd>
                      <dt className={DT}>Description</dt>
                      <dd className={`${DD} min-w-0 lg:col-span-3`}>
                        <textarea
                          readOnly
                          tabIndex={-1}
                          rows={4}
                          value={[clientName, thread.recipient_email].filter(Boolean).join(' · ')}
                          placeholder="—"
                          aria-label="Description"
                          className="ggj-param-readonly-textarea"
                        />
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Hash className="w-3.5 h-3.5" style={{ color: BRAND }} /> Thread Identifiers
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    {([['Thread ID', thread.id], ['Application ID', thread.application_id ?? '—'], ['Org ID', thread.org_id ?? '—']] as [string, string][]).map(([k, v]) => (
                      <><dt key={`k-${k}`} className={DT}>{k}</dt><dd key={`v-${k}`} className={`${DD} font-mono break-all`}>{v}</dd></>
                    ))}
                  </dl>
                </div>
              </div>
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" style={{ color: BRAND }} /> Timing
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    {([['Created at', thread.created_at], ['Sent at', thread.sent_at], ['Opened at', thread.opened_at], ['CTA responded at', thread.cta_responded_at], ['Next follow-up', thread.next_followup_at]] as [string, string | null | undefined][]).map(([k, v]) => (
                      <><dt key={`k-${k}`} className={DT}>{k}</dt><dd key={`v-${k}`} className={DD}>{fmt(v)}</dd></>
                    ))}
                  </dl>
                </div>
              </div>
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" style={{ color: BRAND }} /> Engagement
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    {([['Open count', String(thread.open_count)], ['Follow-up count', String(thread.followup_count ?? 0)], ['Follow-up stopped', thread.followup_stopped ? 'Yes' : 'No'], ['CTA response', thread.cta_response ? CTA_LABEL[thread.cta_response] : '—'], ['Replies', String(replies.length)]] as [string, string][]).map(([k, v]) => (
                      <><dt key={`k-${k}`} className={DT}>{k}</dt><dd key={`v-${k}`} className={DD}>{v}</dd></>
                    ))}
                  </dl>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: sticky sidebar */}
        <aside className="ggj-rail-aside space-y-3 lg:sticky lg:top-4 self-start">

          <CandidateCollapsiblePanel storageKey="email-thread-status" title="Thread Status" defaultOpen>
            <div className="space-y-1.5 px-1">
              <div className="flex items-center justify-center py-2">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold"
                  style={{ background: `${statusColor}18`, color: statusColor }}>
                  {thread.cta_response && CTA_ICON[thread.cta_response]}
                  {statusLabel}
                </span>
              </div>
              {([['Recipient', thread.recipient_name || thread.recipient_email], ['Opens', thread.open_count > 0 ? `${thread.open_count}×` : 'Not opened'], ['Follow-ups', String(thread.followup_count ?? 0)], ['Replies', String(replies.length)], ['Sent', fmtShort(thread.sent_at)]] as [string, string][]).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                  <span className="text-xs vscode-text-tertiary">{k}</span>
                  <span className="text-xs vscode-text-primary font-medium">{v}</span>
                </div>
              ))}
            </div>
          </CandidateCollapsiblePanel>

          <CandidateCollapsiblePanel storageKey="email-thread-context" title="Context" defaultOpen>
            <div className="space-y-1.5 px-1">
              {([['Client', clientName], ['Candidate', app?.candidate?.full_name ?? '—'], ['Job', app?.job?.title ?? '—']] as [string, string][]).map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-2 py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                  <span className="text-xs vscode-text-tertiary shrink-0">{k}</span>
                  <span className="text-xs vscode-text-primary font-medium text-right">{v}</span>
                </div>
              ))}
              {app?.id && (
                <Link to={`/applications/${app.id}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: BRAND }}>
                  <ExternalLink className="w-3 h-3" /> View Application
                </Link>
              )}
            </div>
          </CandidateCollapsiblePanel>

          <CandidateCollapsiblePanel storageKey="email-thread-actions" title="Quick Actions" defaultOpen>
            <div className="space-y-2 px-1">
              {app?.id && (
                <Link to={`/emails/compose?applicationId=${app.id}&replyToThread=${thread.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-colors"
                  style={{ background: `${BRAND}15`, color: BRAND }}>
                  <Reply className="w-4 h-4" /> Reply to Client
                </Link>
              )}
              {canFollowUp && app?.id && (
                <Link to={`/emails/compose?applicationId=${app.id}&replyToThread=${thread.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium border vscode-border vscode-text-secondary hover:vscode-text-primary transition-colors">
                  <Send className="w-4 h-4" /> Send Follow-up
                </Link>
              )}
              {app?.id && (
                <Link to={`/applications/${app.id}`}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium border vscode-border vscode-text-secondary hover:vscode-text-primary transition-colors">
                  <FileText className="w-4 h-4" /> View Application
                </Link>
              )}
            </div>
          </CandidateCollapsiblePanel>

          <CandidateCollapsiblePanel storageKey="email-thread-tips" title="Tips" defaultOpen>
            <div className="space-y-2.5 px-1">
              {[
                { icon: Eye,          text: 'Track when the client opens your email via the open count.' },
                { icon: Zap,          text: 'CTA buttons let the client shortlist, pass, or query directly from the email.' },
                { icon: Reply,        text: 'Send a follow-up if no response after 48 hours.' },
                { icon: AlertCircle,  text: 'Opened but no response — give it 1–2 days before following up.' },
                { icon: Inbox,        text: 'Replies are linked back to this thread for a complete trail.' },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5 vscode-text-tertiary" />
                  <p className="text-xs vscode-text-secondary leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </CandidateCollapsiblePanel>

          <CandidateCollapsiblePanel storageKey="email-thread-links" title="Quick Links" defaultOpen>
            <div className="-mx-4 -mb-3">
              {[
                { label: 'Email Activity',   to: '/emails'       },
                { label: 'Applications',     to: '/applications' },
                ...(app?.id         ? [{ label: 'View Application',              to: `/applications/${app.id}`       }] : []),
                ...(app?.candidate?.id ? [{ label: `Candidate: ${app.candidate.full_name}`, to: `/candidates/${app.candidate.id}` }] : []),
              ].map(({ label, to }) => (
                <Link key={to} to={to}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                  <ArrowRight className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" /> {label}
                </Link>
              ))}
            </div>
          </CandidateCollapsiblePanel>

        </aside>
      </div>
    </div>
  );
}
